import React, { useEffect, useState } from "react";
import {
    Gift,
    Users,
    ListTodo,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Target,
    Search,
    Award,
    Save,
    X,
    AlertTriangle,
    CheckCircle,
    XCircle,
    User,
} from "lucide-react";
import { Select, Button, Switch, Spin, InputNumber, Input, Space, message, Modal } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import bonusApi from "../../api/bonusApi";
import positionSettingsApi from "../../api/positionSettingsApi";
import inventory from "../../api/inventoryApi";
import "../../components/AddUserModal/style.css";

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

const PROMO_TYPES_MAP = {
    onlyCrystals: "Only Crystals",
    crystalsAndCrypto: "Crystals & Crypto",
    cryptoOnly: "Crypto Only",
};

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const selectGlass =
    "w-full [&_.ant-select-selector]:!min-h-[48px] [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!border-white/10 [&_.ant-select-selector]:!bg-white/[0.06] [&_.ant-select-selection-item]:!text-white/90 [&_.ant-select-selection-placeholder]:!text-white/35 [&_.ant-select-arrow]:!text-white/45";

const inputNumGlass =
    "w-full !rounded-xl [&_.ant-input-number-input]:!text-white/90 [&_.ant-input-number-handler-wrap]:!border-white/10";

function promoStatusTone(status) {
    if (status === "ACTIVE") return "border-emerald-400/35 bg-emerald-500/12 text-emerald-100";
    if (status === "PENDING") return "border-amber-400/35 bg-amber-500/12 text-amber-100";
    if (status === "COMPLETED") return "border-sky-400/35 bg-sky-500/12 text-sky-100";
    return "border-white/15 bg-white/[0.08] text-white/65";
}

export default function BonusPage() {
    const [configs, setConfigs] = useState([]);
    const [bonusPromos, setBonusPromos] = useState([]);
    const [positions, setPositions] = useState([]);
    const [gemTypes, setGemTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("positions");
    const [expandedId, setExpandedId] = useState(null);
    const [form, setForm] = useState({ categoryId: "", name: "", maxParticipants: 100, maxTasks: 5, isActive: true });
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [checkTelegramId, setCheckTelegramId] = useState("");
    const [eligibilityResult, setEligibilityResult] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const positionsData = await positionSettingsApi.getAllPositionSettings();
            setPositions(positionsData || []);
        } catch (err) {
            console.error("[BonusPage] positions:", err);
            setPositions([]);
        }
        try {
            const gemsData = await inventory.gemCatalogApi.getAllGems();
            setGemTypes(gemsData || []);
        } catch (err) {
            console.error("[BonusPage] gems:", err);
            setGemTypes([]);
        }
        try {
            const configsData = await bonusApi.getAllConfigs();
            setConfigs(configsData || []);
        } catch (err) {
            console.error("[BonusPage] configs:", err);
            setConfigs([]);
        }
        try {
            const promosData = await bonusApi.getAllBonusPromos();
            setBonusPromos(promosData || []);
        } catch (err) {
            console.error("[BonusPage] promos:", err);
            setBonusPromos([]);
        }
        setLoading(false);
    };

    const getPositionById = (categoryId) => positions.find((p) => p.id === categoryId);

    const getAvailablePositions = () => {
        const usedCategoryIds = configs.map((c) => c.categoryId);
        if (editingId) {
            const currentConfig = configs.find((c) => c.id === editingId);
            return positions.filter((p) => !usedCategoryIds.includes(p.id) || p.id === currentConfig?.categoryId);
        }
        return positions.filter((p) => !usedCategoryIds.includes(p.id));
    };

    const resetForm = () => {
        setForm({ categoryId: "", name: "", maxParticipants: 100, maxTasks: 5, isActive: true });
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = async () => {
        if (!form.categoryId) {
            message.error("Выберите позицию");
            return;
        }
        try {
            if (editingId) {
                await bonusApi.updateConfig(editingId, {
                    name: form.name || null,
                    maxParticipants: parseInt(form.maxParticipants, 10),
                    maxTasks: parseInt(form.maxTasks, 10),
                    isActive: form.isActive,
                });
                message.success("Позиция обновлена");
            } else {
                await bonusApi.createConfig({
                    categoryId: parseInt(form.categoryId, 10),
                    name: form.name || null,
                    maxParticipants: parseInt(form.maxParticipants, 10),
                    maxTasks: parseInt(form.maxTasks, 10),
                    isActive: form.isActive,
                });
                message.success("Позиция создана");
            }
            resetForm();
            fetchData();
        } catch (err) {
            console.error("Ошибка сохранения:", err);
            message.error(err?.response?.data?.message || "Ошибка сохранения");
        }
    };

    const handleEdit = (config) => {
        setForm({
            categoryId: config.categoryId,
            name: config.name || "",
            maxParticipants: config.maxParticipants,
            maxTasks: config.maxTasks,
            isActive: config.isActive,
        });
        setEditingId(config.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        Modal.confirm({
            title: "Удалить позицию?",
            content: "Удалить эту бонусную позицию?",
            okText: "Удалить",
            okType: "danger",
            cancelText: "Отмена",
            className: "custom-modal",
            onOk: async () => {
                try {
                    await bonusApi.deleteConfig(id);
                    message.success("Позиция удалена");
                    fetchData();
                } catch (err) {
                    console.error(err);
                    message.error("Ошибка удаления");
                }
            },
        });
    };

    const handlePositionSelect = (categoryId) => {
        const position = getPositionById(parseInt(categoryId, 10));
        setForm({
            ...form,
            categoryId,
            name: position?.name || form.name,
        });
    };

    const handleCheckEligibility = async () => {
        if (!checkTelegramId) return;
        try {
            const result = await bonusApi.checkEligibilityByTelegram(checkTelegramId);
            setEligibilityResult(result);
        } catch (err) {
            console.error(err);
            setEligibilityResult({ error: err.message });
        }
    };

    const handleResetBonus = async () => {
        if (!checkTelegramId) return;
        Modal.confirm({
            title: "Сбросить все бонусы?",
            content: `Сбросить все использования бонусов для Telegram ID: ${checkTelegramId}?`,
            okText: "Сбросить все",
            cancelText: "Отмена",
            okButtonProps: { danger: true },
            className: "custom-modal",
            onOk: async () => {
                try {
                    const result = await bonusApi.resetBonusByTelegram(checkTelegramId);
                    if (result.success) {
                        message.success(`Сброшено ${result.resetCount} бонус(ов)`);
                        handleCheckEligibility();
                    } else {
                        message.warning(result.message || "Не удалось сбросить");
                    }
                } catch (err) {
                    console.error(err);
                    message.error("Ошибка при сбросе");
                }
            },
        });
    };

    const handleResetBonusCategory = async (categoryId) => {
        if (!checkTelegramId) return;
        const position = getPositionById(categoryId);
        const posName = position?.name || `#${categoryId}`;
        Modal.confirm({
            title: "Сбросить бонус позиции?",
            content: `Сбросить бонус для «${posName}»?`,
            okText: "Сбросить",
            cancelText: "Отмена",
            okButtonProps: { danger: true },
            className: "custom-modal",
            onOk: async () => {
                try {
                    const result = await bonusApi.resetBonusByTelegram(checkTelegramId, categoryId);
                    if (result.success) {
                        message.success(`Бонус для «${posName}» сброшен`);
                        handleCheckEligibility();
                    } else {
                        message.warning(result.message || "Не удалось сбросить");
                    }
                } catch (err) {
                    console.error(err);
                    message.error("Ошибка при сбросе");
                }
            },
        });
    };

    const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

    const th = "whitespace-nowrap px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wide text-white/50 sm:px-4";
    const td = "px-3 py-3 text-[13px] text-white/85 sm:px-4";

    const renderPositionsTab = () => {
        const availablePositions = getAvailablePositions();
        return (
            <>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="flex items-center gap-2 text-[16px] font-semibold text-white/90">
                        <Gift className="h-5 w-5 text-emerald-300/90" />
                        Бонусные позиции
                        <span className="rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[12px] text-white/55">
                            {configs.length}
                        </span>
                    </h2>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            resetForm();
                            setShowForm(true);
                        }}
                        className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                    >
                        Добавить позицию
                    </Button>
                </div>

                {showForm && (
                    <section className={cn(shellClass, "mb-4 p-4 sm:p-5")}>
                        <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
                            <span className="flex items-center gap-2 text-[14px] font-semibold text-white/90">
                                {editingId ? <EditOutlined /> : <PlusOutlined />}
                                {editingId ? "Редактировать позицию" : "Новая бонусная позиция"}
                            </span>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="rounded-lg border border-white/10 p-2 text-white/50 transition hover:bg-white/10 hover:text-white"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {!editingId && positions.length === 0 && (
                            <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-500/10 px-3 py-2 text-[13px] text-amber-100">
                                <AlertTriangle size={16} />
                                Нет позиций. Создайте их в Position Settings.
                            </div>
                        )}
                        {!editingId && positions.length > 0 && availablePositions.length === 0 && (
                            <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-500/10 px-3 py-2 text-[13px] text-amber-100">
                                <AlertTriangle size={16} />
                                Все позиции уже в бонусах.
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <div className="lg:col-span-2">
                                <label className="mb-1.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    <Target size={12} /> Позиция *
                                </label>
                                <Select
                                    className={cn(selectGlass)}
                                    popupClassName="glass-ant-select-dropdown"
                                    placeholder="Поиск по названию или типу…"
                                    value={form.categoryId || undefined}
                                    onChange={(value) => handlePositionSelect(value)}
                                    disabled={!!editingId || availablePositions.length === 0}
                                    size="large"
                                    showSearch
                                    filterOption={(input, option) => {
                                        const pos = availablePositions.find((p) => p.id === option.value);
                                        if (!pos) return false;
                                        const q = input.toLowerCase();
                                        const name = pos.name?.toLowerCase() || "";
                                        const pt = (PROMO_TYPES_MAP[pos.promoType] || pos.promoType)?.toLowerCase() || "";
                                        return name.includes(q) || pt.includes(q);
                                    }}
                                    suffixIcon={<Search size={16} className="text-white/40" />}
                                >
                                    {availablePositions.map((pos) => {
                                        const gem = gemTypes.find((g) => g.id === pos.gemId);
                                        return (
                                            <Select.Option key={pos.id} value={pos.id}>
                                                <div className="flex items-center gap-2">
                                                    {gem?.frontImage && (
                                                        <img
                                                            src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                            alt=""
                                                            className="h-6 w-6 object-contain"
                                                        />
                                                    )}
                                                    <span>
                                                        {pos.name}
                                                        <span className="ml-2 text-[11px] text-white/40">
                                                            ({PROMO_TYPES_MAP[pos.promoType] || pos.promoType})
                                                        </span>
                                                    </span>
                                                </div>
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                                {form.categoryId && (
                                    <PositionPreview position={getPositionById(parseInt(form.categoryId, 10))} gemTypes={gemTypes} />
                                )}
                            </div>
                            <div>
                                <label className="mb-1.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    <Users size={12} /> Макс. участников
                                </label>
                                <InputNumber
                                    className={inputNumGlass}
                                    min={1}
                                    value={form.maxParticipants}
                                    onChange={(value) => setForm({ ...form, maxParticipants: value })}
                                    size="large"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    <ListTodo size={12} /> Макс. задач
                                </label>
                                <InputNumber
                                    className={inputNumGlass}
                                    min={0}
                                    value={form.maxTasks}
                                    onChange={(value) => setForm({ ...form, maxTasks: value })}
                                    size="large"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col gap-4 border-t border-white/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">Статус</span>
                                <div className="mt-2">
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(checked) => setForm({ ...form, isActive: checked })}
                                        checkedChildren="Активна"
                                        unCheckedChildren="Выкл"
                                        className="[&_.ant-switch-checked]:!bg-emerald-500"
                                    />
                                </div>
                            </div>
                            <Button
                                type="primary"
                                icon={<Save size={16} />}
                                onClick={handleSubmit}
                                className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                                size="large"
                            >
                                {editingId ? "Обновить" : "Создать"}
                            </Button>
                        </div>
                    </section>
                )}

                <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                    <table className="w-full min-w-[880px] border-collapse text-left">
                        <thead className="bg-white/[0.05]">
                            <tr className="border-b border-white/10">
                                <th className={cn(th, "w-10")} />
                                <th className={th}>ID</th>
                                <th className={th}>Gem</th>
                                <th className={th}>Название</th>
                                <th className={th}>Тип промо</th>
                                <th className={th}>Участники</th>
                                <th className={th}>Задачи</th>
                                <th className={th}>Статус</th>
                                <th className={cn(th, "text-right")}>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {configs.map((config) => {
                                const position = getPositionById(config.categoryId);
                                const gem = gemTypes.find((g) => g.id === position?.gemId);
                                const isExpanded = expandedId === config.id;
                                return (
                                    <React.Fragment key={config.id}>
                                        <tr
                                            className="cursor-pointer border-t border-white/[0.06] transition-colors hover:bg-white/[0.04]"
                                            onClick={() => toggleExpand(config.id)}
                                        >
                                            <td className={cn(td, "text-center text-white/45")}>
                                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </td>
                                            <td className={cn(td, "font-semibold text-emerald-300/90")}>{config.id}</td>
                                            <td className={td}>
                                                {gem?.frontImage ? (
                                                    <img
                                                        src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                        alt=""
                                                        className="h-10 w-10 object-contain"
                                                    />
                                                ) : (
                                                    <span className="text-white/35">—</span>
                                                )}
                                            </td>
                                            <td className={td}>
                                                <div className="font-medium text-white/90">
                                                    {position?.name || config.name || `Position #${config.categoryId}`}
                                                </div>
                                                <div className="text-[11px] text-white/40">Category: {config.categoryId}</div>
                                            </td>
                                            <td className={td}>
                                                <span className="inline-flex rounded-full border border-sky-400/30 bg-sky-500/12 px-2 py-0.5 text-[11px] text-sky-100">
                                                    {PROMO_TYPES_MAP[position?.promoType] || position?.promoType || "—"}
                                                </span>
                                            </td>
                                            <td className={cn(td, "text-center")}>
                                                <span className="inline-flex items-center justify-center gap-1">
                                                    <Users size={14} className="text-white/35" />
                                                    <span className="font-semibold text-emerald-300/90">{config.maxParticipants}</span>
                                                </span>
                                            </td>
                                            <td className={cn(td, "text-center")}>
                                                <span className="inline-flex items-center justify-center gap-1">
                                                    <ListTodo size={14} className="text-white/35" />
                                                    <span className="font-semibold text-emerald-300/90">{config.maxTasks}</span>
                                                </span>
                                            </td>
                                            <td className={td}>
                                                <span
                                                    className={cn(
                                                        "inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium",
                                                        config.isActive
                                                            ? "border-emerald-400/35 bg-emerald-500/12 text-emerald-100"
                                                            : "border-white/12 bg-white/[0.06] text-white/45"
                                                    )}
                                                >
                                                    {config.isActive ? "Активна" : "Выкл"}
                                                </span>
                                            </td>
                                            <td className={cn(td, "text-right")} onClick={(e) => e.stopPropagation()}>
                                                <Space size="small">
                                                    <Button
                                                        type="text"
                                                        icon={<EditOutlined />}
                                                        onClick={() => handleEdit(config)}
                                                        className="!text-emerald-300 hover:!bg-white/10"
                                                    />
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => handleDelete(config.id)}
                                                        className="hover:!bg-rose-500/10"
                                                    />
                                                </Space>
                                            </td>
                                        </tr>
                                        {isExpanded && position && (
                                            <tr className="border-t border-white/[0.06] bg-white/[0.02]">
                                                <td colSpan={9} className="p-4 sm:p-5">
                                                    <h4 className="mb-4 flex items-center gap-2 text-[14px] font-semibold text-white/90">
                                                        <Award className="h-5 w-5 text-amber-300/90" />
                                                        Детали позиции
                                                    </h4>
                                                    <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                                                        {[
                                                            ["Мин. участников", position.minParticipantsCount],
                                                            ["Мин. призовых мест", position.minPrizePlacesCount],
                                                            [
                                                                "Бюджет по умолч.",
                                                                `${position.defaultBudget} ${position.defaultTokenType}`,
                                                            ],
                                                        ].map(([label, val]) => (
                                                            <div
                                                                key={label}
                                                                className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-center"
                                                            >
                                                                <div className="text-[11px] uppercase tracking-wide text-white/40">
                                                                    {label}
                                                                </div>
                                                                <div className="mt-1 text-lg font-bold text-white/90">{val}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {position.presets?.length > 0 && (
                                                        <>
                                                            <h5 className="mb-2 flex items-center gap-2 text-[13px] font-medium text-white/55">
                                                                <Target size={14} /> Presets
                                                            </h5>
                                                            <div className="overflow-x-auto rounded-lg border border-white/[0.08]">
                                                                <table className="w-full min-w-[400px] border-collapse text-[13px]">
                                                                    <thead className="bg-white/[0.05] text-[11px] text-white/50">
                                                                        <tr className="border-b border-white/10">
                                                                            <th className="px-3 py-2 text-left font-semibold">#</th>
                                                                            <th className="px-3 py-2 text-left font-semibold">Иконка</th>
                                                                            <th className="px-3 py-2 text-left font-semibold">Победители</th>
                                                                            <th className="px-3 py-2 text-left font-semibold">Бюджет</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {position.presets.map((preset, idx) => {
                                                                            const pos = position.presets.length - idx;
                                                                            const iconGem = gemTypes.find((g) => g.position === pos);
                                                                            return (
                                                                                <tr
                                                                                    key={idx}
                                                                                    className="border-t border-white/[0.06] hover:bg-white/[0.03]"
                                                                                >
                                                                                    <td className="px-3 py-2 font-semibold text-emerald-300/90">
                                                                                        {pos}
                                                                                    </td>
                                                                                    <td className="px-3 py-2">
                                                                                        {iconGem?.frontImage ? (
                                                                                            <img
                                                                                                src={`${IMAGE_BASE_URL}${iconGem.frontImage}`}
                                                                                                alt=""
                                                                                                className="h-8 w-8 object-contain"
                                                                                            />
                                                                                        ) : (
                                                                                            "—"
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="px-3 py-2 text-white/75">
                                                                                        {preset.win.toFixed(2)}%
                                                                                    </td>
                                                                                    <td className="px-3 py-2 font-medium text-emerald-300/85">
                                                                                        {preset.budget_percent.toFixed(2)}%
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    {configs.length === 0 && (
                        <div className="flex flex-col items-center py-16 text-white/45">
                            <Gift className="mb-2 h-12 w-12 opacity-40" />
                            <p className="text-[13px]">Бонусных позиций нет</p>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderPromosTab = () => (
        <>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="flex items-center gap-2 text-[16px] font-semibold text-white/90">
                    <Award className="h-5 w-5 text-amber-300/90" />
                    Бонусные промо
                    <span className="rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[12px] text-white/55">
                        {bonusPromos.length}
                    </span>
                </h2>
                <Button
                    icon={<RefreshCw size={16} />}
                    onClick={fetchData}
                    className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/80 hover:!border-white/20 hover:!bg-white/10"
                >
                    Обновить
                </Button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                <table className="w-full min-w-[720px] border-collapse text-left">
                    <thead className="bg-white/[0.05]">
                        <tr className="border-b border-white/10">
                            {["Promo ID", "Название", "Создатель", "Статус", "Участники", "Тип", "Бонус использован"].map((h) => (
                                <th key={h} className={th}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {bonusPromos.map((promo) => (
                            <tr key={promo.id} className="border-t border-white/[0.06] hover:bg-white/[0.03]">
                                <td className={cn(td, "font-semibold text-emerald-300/90")}>#{promo.id}</td>
                                <td className={td}>{promo.title || "—"}</td>
                                <td className={td}>{promo.creatorUserId}</td>
                                <td className={td}>
                                    <span
                                        className={cn(
                                            "inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium",
                                            promoStatusTone(promo.status)
                                        )}
                                    >
                                        {promo.status}
                                    </span>
                                </td>
                                <td className={cn(td, "text-center font-semibold text-emerald-300/90")}>
                                    {promo.participantsCount}
                                </td>
                                <td className={td}>
                                    <span className="inline-flex rounded-full border border-sky-400/30 bg-sky-500/12 px-2 py-0.5 text-[11px] text-sky-100">
                                        {PROMO_TYPES_MAP[promo.promoType] || promo.promoType}
                                    </span>
                                </td>
                                <td className={cn(td, "text-[12px] text-white/45")}>
                                    {promo.bonusUsedAt ? new Date(promo.bonusUsedAt).toLocaleString("ru-RU") : "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {bonusPromos.length === 0 && (
                    <div className="flex flex-col items-center py-16 text-white/45">
                        <Gift className="mb-2 h-12 w-12 opacity-40" />
                        <p className="text-[13px]">Бонусных промо нет</p>
                    </div>
                )}
            </div>
        </>
    );

    const renderCheckTab = () => (
        <div className="mx-auto w-full max-w-xl">
            <div className="mb-4">
                <label className="mb-2 block text-[11px] font-medium uppercase tracking-wide text-white/40">
                    Telegram ID пользователя
                </label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                        placeholder="Введите Telegram ID…"
                        value={checkTelegramId}
                        onChange={(e) => setCheckTelegramId(e.target.value)}
                        onPressEnter={handleCheckEligibility}
                        prefix={<Search size={16} className="text-white/35" />}
                        className="!rounded-xl !border-white/10 !bg-white/[0.06] sm:max-w-xs"
                    />
                    <Button
                        type="primary"
                        onClick={handleCheckEligibility}
                        disabled={!checkTelegramId}
                        className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                    >
                        Проверить
                    </Button>
                </div>
            </div>

            {eligibilityResult && (
                <div
                    className={cn(
                        shellClass,
                        "p-4 sm:p-5",
                        eligibilityResult.error && "border-rose-400/25 bg-rose-500/[0.06]"
                    )}
                >
                    {eligibilityResult.error ? (
                        <div className="flex gap-3">
                            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-400" />
                            <div>
                                <div className="font-semibold text-rose-200">
                                    {eligibilityResult.error === "User not found"
                                        ? "Пользователь не найден"
                                        : "Ошибка"}
                                </div>
                                <p className="mt-1 text-[13px] text-white/50">
                                    {eligibilityResult.error === "User not found"
                                        ? "Пользователь с таким Telegram ID не зарегистрирован."
                                        : eligibilityResult.error}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {eligibilityResult.user && (
                                <div className="flex gap-3 border-b border-white/10 pb-4">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-400/30 bg-emerald-500/15 text-emerald-200">
                                        <User size={20} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-white/95">
                                            {eligibilityResult.user.firstName} {eligibilityResult.user.lastName}
                                        </div>
                                        {eligibilityResult.user.username && (
                                            <div className="text-[13px] text-emerald-300/90">
                                                @{eligibilityResult.user.username}
                                            </div>
                                        )}
                                        <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-white/40">
                                            <span>ID: {eligibilityResult.user.id}</span>
                                            <span>TG: {eligibilityResult.user.telegramId}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div
                                    className={cn(
                                        "flex items-center gap-3 rounded-xl border px-3 py-3",
                                        eligibilityResult.eligible
                                            ? "border-emerald-400/30 bg-emerald-500/10"
                                            : "border-rose-400/30 bg-rose-500/10"
                                    )}
                                >
                                    {eligibilityResult.eligible ? (
                                        <CheckCircle className="h-5 w-5 text-emerald-400" />
                                    ) : (
                                        <XCircle className="h-5 w-5 text-rose-400" />
                                    )}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wide text-white/40">Право на бонус</div>
                                        <div className="text-[14px] font-semibold text-white/90">
                                            {eligibilityResult.eligible ? "Доступен" : "Недоступен"}
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className={cn(
                                        "flex flex-wrap items-center gap-3 rounded-xl border px-3 py-3",
                                        eligibilityResult.used
                                            ? "border-amber-400/30 bg-amber-500/10"
                                            : "border-white/10 bg-white/[0.04]"
                                    )}
                                >
                                    {eligibilityResult.used ? (
                                        <AlertTriangle className="h-5 w-5 text-amber-400" />
                                    ) : (
                                        <CheckCircle className="h-5 w-5 text-white/35" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[10px] uppercase tracking-wide text-white/40">Использование</div>
                                        <div className="text-[14px] font-semibold text-white/90">
                                            {eligibilityResult.used
                                                ? `Использовано ${eligibilityResult.usedCategories?.length || 0} поз.`
                                                : "Не использован"}
                                        </div>
                                    </div>
                                    {eligibilityResult.used && (
                                        <Button size="small" danger onClick={handleResetBonus} className="!rounded-lg shrink-0">
                                            Сбросить все
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {eligibilityResult.usedCategories?.length > 0 && (
                                <div className="border-t border-white/10 pt-4">
                                    <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-amber-200/90">
                                        <AlertTriangle size={14} />
                                        Использованные ({eligibilityResult.usedCategories.length})
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {eligibilityResult.usedCategories.map((used, idx) => {
                                            const position = getPositionById(used.categoryId);
                                            const gem = gemTypes.find((g) => g.id === position?.gemId);
                                            return (
                                                <div
                                                    key={idx}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-2 py-1.5 text-[12px]"
                                                >
                                                    {gem?.frontImage && (
                                                        <img
                                                            src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                            alt=""
                                                            className="h-6 w-6 object-contain"
                                                        />
                                                    )}
                                                    <span className="font-medium text-white/90">
                                                        {position?.name || `#${used.categoryId}`}
                                                    </span>
                                                    <span className="text-white/40">#{used.promoId}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleResetBonusCategory(used.categoryId)}
                                                        className="ml-1 rounded p-1 text-rose-400 hover:bg-rose-500/20"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {eligibilityResult.availablePositions?.length > 0 && (
                                <div className="border-t border-white/10 pt-4">
                                    <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-white/45">
                                        <Award size={14} className="text-emerald-400/80" />
                                        Доступные ({eligibilityResult.availablePositions.length})
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {eligibilityResult.availablePositions.map((pos) => {
                                            const position = getPositionById(pos.categoryId);
                                            const gem = gemTypes.find((g) => g.id === position?.gemId);
                                            return (
                                                <div
                                                    key={pos.id}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/25 bg-emerald-500/10 px-2 py-1.5 text-[12px]"
                                                >
                                                    {gem?.frontImage && (
                                                        <img
                                                            src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                            alt=""
                                                            className="h-6 w-6 object-contain"
                                                        />
                                                    )}
                                                    <span className="font-medium text-white/90">
                                                        {position?.name || `#${pos.categoryId}`}
                                                    </span>
                                                    <span className="text-white/40">
                                                        до {pos.maxParticipants} уч. · {pos.maxTasks} задач
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-fuchsia-500/10 blur-3xl" />
                <div className="relative flex items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                        <Gift className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                    </span>
                    <div>
                        <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">Бонусы</h1>
                        <p className="text-[12px] text-white/45">Позиции, промо и проверка eligibility</p>
                    </div>
                </div>
            </section>

            <div className="flex flex-wrap gap-2 rounded-xl border border-white/10 bg-white/[0.05] p-1 sm:flex-nowrap">
                {[
                    { id: "positions", icon: Target, label: "Позиции" },
                    { id: "promos", icon: Award, label: `Промо (${bonusPromos.length})` },
                    { id: "check", icon: Search, label: "Проверка" },
                ].map(({ id, icon: Icon, label }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setTab(id)}
                        className={cn(
                            "flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-medium transition-colors sm:min-w-[8rem]",
                            tab === id
                                ? "bg-white/[0.14] text-white shadow-inner"
                                : "text-white/50 hover:bg-white/[0.06] hover:text-white/85"
                        )}
                    >
                        <Icon size={18} className="opacity-90" />
                        {label}
                    </button>
                ))}
            </div>

            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                    {tab === "positions" && renderPositionsTab()}
                    {tab === "promos" && renderPromosTab()}
                    {tab === "check" && renderCheckTab()}
                </div>
            </section>
        </div>
    );
}

function PositionPreview({ position, gemTypes }) {
    if (!position) return null;
    const gem = gemTypes.find((g) => g.id === position.gemId);
    return (
        <div className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-500/[0.07] p-3">
            <div className="flex items-center gap-3">
                {gem?.frontImage && (
                    <img src={`${IMAGE_BASE_URL}${gem.frontImage}`} alt="" className="h-12 w-12 object-contain" />
                )}
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-white/90">{position.name}</div>
                    <div className="text-[11px] text-white/45">
                        {PROMO_TYPES_MAP[position.promoType] || position.promoType} · мин.{" "}
                        {position.minParticipantsCount} уч.
                    </div>
                </div>
            </div>
        </div>
    );
}
